import { BsCheckCircle } from 'react-icons/bs';
import { Badge, Button } from 'react-bootstrap';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import styled from '@emotion/styled';
import { css } from 'twin.macro';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Skeleton from 'react-loading-skeleton';
import { connect } from 'react-redux';
import React, { Fragment, useMemo, useState } from 'react';
import { VscLoading } from 'react-icons/vsc';
import { toast } from 'react-toastify';
import { fetchWrapper } from '@/_helpers';
import { RxTriangleDown } from 'react-icons/rx';
import Swal from 'sweetalert2';

const StepperContainer = styled.div`
    position: relative;
`;

const StepperStep = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    padding: 0 8px;

    @media (max-width: 768px) {
        padding: 0 4px;
    }
`;

const StepConnector = styled.div`
    position: absolute;
    top: 16px;
    left: calc(50% + 16px);
    right: calc(-50% + 16px);
    height: 2px;
    background-color: ${(props) => (props.isDone ? '#28a745' : '#e9ecef')};
    z-index: 1;
`;

const StepButtonWrapper = styled.div`
    margin-bottom: 0.5rem;
    position: relative;
    z-index: 2;
`;

const StepButton = styled(Button)`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: ${(props) => (props.isDone ? '#28a745' : '#f8f9fa')};
    color: ${(props) => (props.isDone ? 'white' : '#6c757d')};
    border-width: ${(props) => (props.isCurrent ? '4px' : '2px')};
    border-color: ${(props) => (props.isCurrent ? '#50aeb0' : 'white')};
    border-style: solid;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        background-color: ${(props) => (props.isDone ? '#28a745' : '#f8f9fa')};
        color: ${(props) => (props.isDone ? 'white' : '#6c757d')};
    }

    @media (max-width: 768px) {
        width: 28px !important;
        height: 28px !important;
    }
`;

const CheckIcon = styled(BsCheckCircle)`
    position: absolute;
    bottom: -4px;
    right: -4px;
    color: #28a745;
    background-color: white;
    border-radius: 50%;
    font-size: 18px;
`;

const ExclamationIcon = styled(HiOutlineExclamationCircle)`
    position: absolute;
    bottom: -4px;
    right: -4px;
    color: #ea9941;
    background-color: white;
    border-radius: 50%;
    font-size: 18px;
`;

const StepContent = styled.div`
    text-align: center;
    padding-left: 8px;
    padding-right: 8px;
    cursor: pointer;
    max-width: 120px;

    &:hover .event-title {
        color: #50aeb0 !important;
    }
`;

const EventTitle = styled.div`
    font-weight: ${(props) => (props.isCurrent ? '600' : '400')};
    color: ${(props) => (props.isDone ? '#28a745' : '#6c757d')};
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    margin-bottom: 0.25rem;
    transition: color 0.2s ease;
`;

const Component = React.memo(({ isLoading, plan, currUser, data, setData = () => {} }) => {
    const [isLoadingTask, setIsLoadingTask] = useState(null);
    const packageEvents = useMemo(() => {
        if (Array.isArray((plan || {}).taskData)) return plan.taskData;
        return [];
    }, [plan]);
    if (isLoading) {
        return <Skeleton height={150} className="mb-4 p-4" />;
    }
    if (!plan || !plan?.taskData || !currUser) {
        return null;
    }
    const handleTaskClick = async (task) => {
        try {
            if (isLoadingTask || isLoading) return;
            const taskId = task?._id || task?.id;
            setIsLoadingTask(taskId);
            if (!taskId) return;
            Swal.fire({
                html: `<div style="text-align: center;"><h3>Please wait...</h3></div>`,
                showConfirmButton: false,
                allowOutsideClick: false,
            });
            const response = await fetchWrapper.get(`task/${taskId}`);
            setData(response);
        } catch (error) {
            console.error(error);
            if (error?.name === 'AbortError') return;
            toast.error('Failed to switch event');
        } finally {
            setIsLoadingTask(null);
            Swal.close();
        }
    };

    const completedEvents = packageEvents.filter((e) => (e.status || []).includes(currUser?._id));

    return (
        <div
            className="mb-4"
            css={css`
                box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
                padding: 8px;
                border-radius: 4px;
            `}
        >
            <div className="d-flex align-items-center mb-4">
                <Badge variant="warning" className="mr-2">
                    Plan
                </Badge>
                <Badge variant="light">{plan?.name}</Badge>
                <Badge variant="light" className="ml-auto">
                    {completedEvents.length}/{packageEvents.length} Completed
                </Badge>
            </div>

            {packageEvents.length > 0 && (
                <StepperContainer>
                    <div className="d-flex justify-content-between align-items-start">
                        {packageEvents.map((event, index) => {
                            const eventId = event._id || event.id;
                            const dataId = data._id || data.id;
                            const isCurrent = eventId === dataId;
                            const isDone = (event.status || []).includes(currUser?._id);
                            const isLast = index === packageEvents.length - 1;

                            return (
                                <StepperStep key={eventId}>
                                    {!isLast && <StepConnector isDone={isDone} />}
                                    {isCurrent && (
                                        <RxTriangleDown
                                            css={css`
                                                margin-top: -16px;
                                            `}
                                        />
                                    )}

                                    <StepButtonWrapper>
                                        {isLoadingTask === eventId ? (
                                            <VscLoading className="spin" size={36} color={'#50aeb0'} />
                                        ) : (
                                            <Fragment>
                                                <StepButton
                                                    isDone={isDone}
                                                    isCurrent={isCurrent}
                                                    variant="outline-primary"
                                                    onClick={() => handleTaskClick(event)}
                                                    type="button"
                                                    size="lg"
                                                    disabled={isLoadingTask}
                                                >
                                                    {index + 1}
                                                </StepButton>
                                            </Fragment>
                                        )}

                                        {isDone ? <CheckIcon /> : <ExclamationIcon />}
                                    </StepButtonWrapper>

                                    <StepContent onClick={() => handleTaskClick(event)} disabled={isLoadingTask}>
                                        <EventTitle isDone={isDone} isCurrent={isCurrent} className="event-title">
                                            {event.title || ''}
                                        </EventTitle>
                                    </StepContent>
                                </StepperStep>
                            );
                        })}
                    </div>
                </StepperContainer>
            )}
        </div>
    );
});

const EventPlanProgress = (props) => {
    return (
        <ComponentErrorBoundary>
            <Component {...props} />
        </ComponentErrorBoundary>
    );
};
const mapStateToProps = (state) => ({
    currUser: state.user.user,
});
export default connect(mapStateToProps, {})(EventPlanProgress);
